
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { AuthButtons } from "./AuthButtons";
import { popularLocations, otherLocations, resourceItems, companyItems } from "./navData";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const [nursingHomesOpen, setNursingHomesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="lg:hidden bg-white border-t shadow-lg fixed top-[72px] left-0 right-0 z-40 max-h-[calc(100vh-72px)] overflow-y-auto">
      <div className="container py-6">
        <div className="space-y-5">
          {/* Nursing Homes Section */}
          <div>
            <button 
              onClick={() => setNursingHomesOpen(!nursingHomesOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <h3 className="font-medium text-gray-800 font-lora">Nursing Homes</h3>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  nursingHomesOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {nursingHomesOpen && (
              <div className="py-4 pl-4 space-y-4 animate-fade-in">
                <h4 className="text-xs uppercase tracking-wide text-grandpal-green font-medium mb-2">Popular locations</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {popularLocations.map((item) => (
                    <Link
                      key={item.title}
                      to={item.path}
                      className="block py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                      onClick={onClose}
                    >
                      {item.title}
                    </Link>
                  ))}
                  {otherLocations.map((item) => (
                    <Link
                      key={item.title}
                      to={item.path}
                      className="block py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                      onClick={onClose}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/nursing-homes"
                  className="flex items-center text-grandpal-green font-medium hover:text-grandpal-green-dark mt-2 group"
                  onClick={onClose}
                >
                  View all Nursing Homes
                  <ArrowRight className="ml-1.5 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            )}
          </div>

          {/* Resources Section */}
          <div>
            <button 
              onClick={() => setResourcesOpen(!resourcesOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <h3 className="font-medium text-gray-800 font-lora">Resources</h3>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  resourcesOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {resourcesOpen && (
              <div className="py-4 pl-4 space-y-3 animate-fade-in">
                {resourceItems.map((item) => (
                  <div key={item.title} className="flex items-start py-2">
                    <div className="mr-3 mt-0.5 bg-grandpal-green-light p-2 rounded-md text-grandpal-green">
                      {item.icon}
                    </div>
                    <div>
                      <Link
                        to={item.path}
                        className="block font-medium text-gray-700 hover:text-grandpal-green transition-colors"
                        onClick={onClose}
                      >
                        {item.title}
                      </Link>
                      <p className="text-sm text-grandpal-gray mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Company Section */}
          <div>
            <button 
              onClick={() => setCompanyOpen(!companyOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <h3 className="font-medium text-gray-800 font-lora">Company</h3>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  companyOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {companyOpen && (
              <div className="py-4 pl-4 space-y-3 animate-fade-in">
                {companyItems.map((item) => (
                  <div key={item.title} className="flex items-start py-2">
                    <div className="mr-3 mt-0.5 bg-grandpal-green-light p-2 rounded-md text-grandpal-green">
                      {item.icon}
                    </div>
                    <div>
                      <Link
                        to={item.path}
                        className="block font-medium text-gray-700 hover:text-grandpal-green transition-colors"
                        onClick={onClose}
                      >
                        {item.title}
                      </Link>
                      <p className="text-sm text-grandpal-gray mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Guide link */}
          <div>
            <Link
              to="/guides"
              className="block py-3 border-b font-medium text-gray-800 hover:text-grandpal-green transition-colors font-lora"
              onClick={onClose}
            >
              Guide
            </Link>
          </div>

          {/* Waitlist Section */}
          <div className="bg-gradient-to-br from-white to-grandpal-beige p-5 rounded-lg my-5 relative overflow-hidden border border-grandpal-beige-dark shadow-sm">
            <div className="relative z-10">
              <div className="flex gap-4 items-start">
                <div>
                  <h3 className="text-grandpal-green text-lg font-bold mb-3 font-lora">Join a waitlist for free</h3>
                  <p className="text-gray-700 text-sm mb-4 max-w-[280px]">
                    Sign up to a waitlist to boost your odds of finding the right nursing home quicker.
                  </p>
                  <Link
                    to="/waitlist"
                    className="flex items-center text-white font-medium group bg-grandpal-green px-4 py-2.5 rounded-md shadow-sm hover:bg-grandpal-green-dark transition-colors"
                    onClick={onClose}
                  >
                    Join a Waitlist
                    <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="flex-none">
                  <img 
                    src="/lovable-uploads/b4d6b12f-537c-4411-8f7c-9770afaa4de2.png" 
                    alt="Coffee and cookies" 
                    className="h-24 w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Auth buttons */}
          <div className="mt-5 pb-2">
            <AuthButtons />
          </div>
        </div>
      </div>
    </div>
  );
}

